import { OnlineOrderFilterForm } from 'views/OnlineStore/hooks/useOnlineOrder'
import httpRequest from 'httpRequest'
import {
    CompOrderLineRequestContract,
    KitchenPrintErrorLogContract,
    OnlineOrderLookupContract,
    OrderContract,
    OrderDetailContract,
    OrderLineKdsStatus,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderLookupContract,
    OrderOwnerContract,
    ProcessPaymentContract,
    RestockOrderLineContract,
    UnreadOrder,
    VoidMultipleOrdersRequestContract,
    VoidOrderLineRequestContract,
    VoidOrderRequestContract,
} from 'models'
import { searchOrderRequest } from 'store/actions/orderLookup.action'
import { CountModel } from 'models/CountModel'
import { ProcessedOrderContract } from 'models/ProcessedOrderContract'

const ORDER_PREFIX = '/location/orders'

export const toVoidOrderRequest = (
    order: OrderContract,
    permissionToken: string | undefined | null,
    restockOrderLines: RestockOrderLineContract[],
): VoidOrderRequestContract => {
    if (!order.voidComp) throw new Error('no defined void details')

    return {
        orderID: order.orderID!,
        voidReason: {
            reasonID: order.voidComp.voidCompReasonID!,
            reason: order.voidComp.name,
            note: order.voidComp.note,
        },
        permissionToken,
        restockOrderLines,
    }
}

export interface OrderLineSearchRequest {
    tableGroupID?: string
    tableIDs?: string[]
    status?: OrderLineStatus
    kdsStatus?: OrderLineKdsStatus
    orderIDs?: string[]
    isParentLine?: boolean
    includeOrder?: boolean
}

const orderService = {
    searchOrders(searchRequest: searchOrderRequest) {
        return httpRequest.get<OrderLookupContract[]>(`${ORDER_PREFIX}`, {
            params: searchRequest,
        })
    },
    getOrder(orderID: string) {
        return httpRequest.get<OrderDetailContract>(
            `${ORDER_PREFIX}/${orderID}`,
        )
    },
    voidOrder(order: VoidOrderRequestContract) {
        return httpRequest.post(`${ORDER_PREFIX}/void`, order)
    },
    voidMultipleOrders(contract: VoidMultipleOrdersRequestContract) {
        return httpRequest.post(`${ORDER_PREFIX}/void-multiple`, contract)
    },
    voidOrderLines(
        orderID: string,
        voidContract: VoidOrderLineRequestContract,
    ) {
        return httpRequest.post<OrderContract>(
            `${ORDER_PREFIX}/${orderID}/void-order-lines`,
            voidContract,
        )
    },
    processOrder(order: OrderContract) {
        return httpRequest.post<ProcessedOrderContract>(ORDER_PREFIX, order)
    },
    processAndSendOrder(order: OrderContract) {
        return httpRequest.post<ProcessedOrderContract>(
            `${ORDER_PREFIX}/send`,
            order,
        )
    },
    sendOrder: (orderID: string) =>
        httpRequest.post<OrderContract>(`${ORDER_PREFIX}/${orderID}/send`),
    getPendingOrders: (orderIDs: string[]) =>
        httpRequest.get<OrderLookupContract[]>(
            `${ORDER_PREFIX}/pending-orders`,
            {
                params: { orderIDs },
            },
        ),
    finishCookingOrder: (orderID: string) =>
        httpRequest.post<OrderDetailContract>(
            `${ORDER_PREFIX}/${orderID}/complete-cooking`,
        ),
    completeOrders: (orderIDs: string[]) =>
        httpRequest.post(`${ORDER_PREFIX}/force-complete`, orderIDs),
    completePickupOrDelivery: (orderID: string) =>
        httpRequest.post<OrderLookupContract>(
            `${ORDER_PREFIX}/${orderID}/complete-pickup-or-delivery`,
        ),
    syncOfflineOrder: (
        order: OrderContract,
        payments: ProcessPaymentContract[] | null,
    ) =>
        httpRequest.post(`${ORDER_PREFIX}/offline-sync`, {
            order,
            payments,
        }),
    // Table Service APIs
    getTableOrders: (tableID: string, includeAllSibling: boolean = false) =>
        httpRequest.get<OrderOwnerContract[]>(
            `${ORDER_PREFIX}/by-table/${tableID}`,
            { params: { includeAllSibling } },
        ),

    getTableGroupOrders: (tableGroupID: string) =>
        httpRequest.get<OrderOwnerContract[]>(
            `${ORDER_PREFIX}/by-table-group/${tableGroupID}`,
        ),
    getOrderlines: (searchRequest: OrderLineSearchRequest) =>
        httpRequest.get<OrderLineLookupContract[]>(
            `${ORDER_PREFIX}/order-lines`,
            {
                params: searchRequest,
            },
        ),
    // get online orders
    getOnlineOrders: (searchRequest: OnlineOrderFilterForm) =>
        httpRequest.get<OnlineOrderLookupContract[]>(
            `${ORDER_PREFIX}/online-orders`,
            {
                params: {
                    customerName: searchRequest.customerName,
                    brandID: searchRequest.brandID,
                    startDate: searchRequest.dateRange[0]?.toISOString(),
                    endDate: searchRequest.dateRange[1]?.toISOString(),
                    onlineOrderChannelIDs: searchRequest.onlineOrderChannelIDs,
                    onlineStoreIDs: searchRequest.onlineStoreIDs,
                },
            },
        ),
    getOnlineOrderByID: (orderID: string) =>
        httpRequest.get<OnlineOrderLookupContract>(
            `${ORDER_PREFIX}/online-order-by-id/${orderID}`,
        ),
    getOnlineOrderByOrderNo: (orderNo: string) =>
        httpRequest.get<OnlineOrderLookupContract>(
            `${ORDER_PREFIX}/online-order-by-order-no/${orderNo}`,
        ),
    // online order notification
    getUnreadOrders: () =>
        httpRequest.get<UnreadOrder[]>(`${ORDER_PREFIX}/unread`),
    getUnreadOrderCount: () =>
        httpRequest.get<CountModel>(`${ORDER_PREFIX}/unread-count`),
    markOrderAsRead: (orderID: string) =>
        httpRequest.put(`${ORDER_PREFIX}/mark-read/${orderID}`),
    reprintOrderLine: (orderLineIDs: string[]) =>
        httpRequest.put<void>(`${ORDER_PREFIX}/re-print`, orderLineIDs),
    /**
     * We call this endpoint if the comp amount doesn't match original the sent order line's full quantity.
     */
    compOrderLines(orderID: string, payload: CompOrderLineRequestContract) {
        return httpRequest.post(
            `${ORDER_PREFIX}/${orderID}/comp-order-lines`,
            payload,
        )
    },

    /**
     * get kitchen print error details
     * @param orderLineIds list of order line Ids to query for error details
     */
    getKitchenPrintErrorDetails(orderLineIds: string[]) {
        // use POST request to not aware the limit length in HTTP Querystring
        return httpRequest.post<KitchenPrintErrorLogContract[]>(
            `${ORDER_PREFIX}/kitchen-print-error-details/`,
            orderLineIds,
        )
    },
}

export default orderService
